/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : authSlice.ts
 *
 * Project Title : APM Settings Tool
 *
 * Description   : Redux Slice for Auth state
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H422337
 *
 * @Date         : December 29, 2021
 *
 * @Version      : Version 1.0
 *
 * By            :
 * On            :
 * Description   :
 *
 *
 ******************************************************************************/

import axios from "axios";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk, RootState } from "stores/GlobalStore";
import { getToken } from "api";
import {
  EnumTokenType,
  IAuthState,
  ITokenState,
  IUserInfoState,
  IGetAuthTokenRequestArgs,
  IGetRefreshTokenRequestArgs,
} from "models/auth";
import { IAppConfig, decodeToken } from "shared/utils";

interface ITokenReceivedPayload {
  token: ITokenState;
  userInfo: IUserInfoState;
}
const initialState: IAuthState = {
  token: null,
  userInfo: null,
  userContactDetails: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    tokenReceived(
      authState: IAuthState,
      action: PayloadAction<ITokenReceivedPayload>
    ) {
      authState.token = action.payload.token;
      authState.userInfo = action.payload.userInfo;
    },
  },
});

export const authReducer = authSlice.reducer;

export const setTimeoutThunk =
  (appConfig: IAppConfig): AppThunk =>
  (dispatch, getState) => {
    // eslint-disable-next-line
    const { refresh_token, expires_in } = getState().auth.token!;
    const args = { type: EnumTokenType.Refresh, refresh_token, appConfig };

    window.setTimeout(
      () => dispatch(fetchToken<IGetRefreshTokenRequestArgs>(args)),
      (expires_in - 10) * 1000
    );
  };

export const fetchToken =
  <T extends IGetAuthTokenRequestArgs | IGetRefreshTokenRequestArgs>(
    args: T
  ): AppThunk =>
  async (dispatch) => {
    delete axios.defaults.headers.common["Authorization"];
    delete axios.defaults.headers.common["user-id"];

    const token: ITokenState = await getToken<T>(args);

    const { avatar, family_name, given_name, name, customer_sub } = decodeToken(
      token.id_token
    ) as IUserInfoState;
    const userInfo: IUserInfoState = {
      avatar,
      family_name,
      given_name,
      name,
      customer_sub,
    };

    axios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${token.access_token}`;
    axios.defaults.headers.common["user-id"] = customer_sub;
    dispatch(authSlice.actions.tokenReceived({ token, userInfo }));
    dispatch(setTimeoutThunk(args.appConfig));
  };

export const selectIsAuthenticated = (state: RootState): boolean =>
  state.auth.token !== null;
export const selectToken = (state: RootState): ITokenState | null =>
  state.auth.token;
export const selectUserInfo = (state: RootState): IUserInfoState | null =>
  state.auth.userInfo;
